import { env } from '@/env/client';
import { ConvexAuthProvider } from '@convex-dev/auth/react';
import { ConvexQueryClient } from '@convex-dev/react-query';
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConvexReactClient } from 'convex/react';
import { Loader2 } from 'lucide-react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './i18n/i18n.ts';
import './index.css';

/** Dev-only: suppress React PDF license warning when prod key doesn't cover localhost. */
if (import.meta.env.DEV) {
  const originalWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    const msg = args
      .map((a) => (typeof a === 'string' ? a : String(a)))
      .join(' ');
    if (/license key.*(?:domain|IP)|React PDF.*license/i.test(msg)) return;
    originalWarn.apply(console, args);
  };
}

// Configure Sentry
Sentry.init(
  {
    dsn: env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
  },
  // Forward the init method from @sentry/react
  SentryReact.init,
);

const convex = new ConvexReactClient(env.VITE_CONVEX_URL ?? '');

const convexQueryClient = new ConvexQueryClient(convex);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
    },
  },
});
convexQueryClient.connect(queryClient);

createRoot(document.getElementById('root') as HTMLElement).render(
  <NuqsAdapter>
    <QueryClientProvider client={queryClient}>
      <ConvexAuthProvider client={convex}>
        <Suspense
          fallback={
            <div>
              <Loader2 className="animate-spin" />
            </div>
          }>
          <App />
        </Suspense>
      </ConvexAuthProvider>
    </QueryClientProvider>
  </NuqsAdapter>,
);
